<template>
  <main class="wallet-transfer-success-page">
    <button
      class="transfer-success-back"
      type="button"
      :aria-label="t('home.home')"
      @click="goHome"
    >
      <AppSvgIcon
        class="transfer-success-back__icon"
        :src="backIcon"
        size="17"
      />
    </button>

    <section class="transfer-success-content">
      <div class="transfer-success-illustration">
        <AppSvgIcon
          class="transfer-success-illustration__glow"
          :src="glowImage"
          size="108.2"
        />
        <div class="transfer-success-illustration__frame">
          <img :src="successImage" alt="" />
        </div>
      </div>

      <div class="transfer-success-copy">
        <h1>
          <span>{{ t("wallet.transferFriendSuccessTitle") }}</span>
          <span v-if="sentAmountText">{{ sentAmountText }}</span>
        </h1>
        <p v-if="recipientText">{{ recipientText }}</p>
      </div>
    </section>

    <p class="transfer-success-history-hint">
      {{ t("wallet.transferViewFlowHint") }}
    </p>
  </main>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { getRouteState } from "@/utils/route-state";
import backIcon from "@/assets/images/SVG/icon/transfer-submitted-back.svg";
import glowImage from "@/assets/images/SVG/icon/transfer-submitted-glow.svg";
import successImage from "@/assets/images/transfer/transfer-success.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const pageState = computed(() =>
  getRouteState(route, "wallet-transfer-success", {}),
);

const sentAmountText = computed(() => {
  const amount = String(pageState.value.amount || "").trim();
  const symbol = String(pageState.value.symbol || "").trim();
  return amount && symbol
    ? t("wallet.transferFriendSentAmount", { amount, symbol })
    : "";
});

const recipientText = computed(() => {
  const userId = String(pageState.value.userId || "").trim();
  return userId ? t("wallet.transferFriendRecipient", { userId }) : "";
});

function goHome() {
  router.replace("/home/index");
}
</script>

<style scoped lang="scss">
.wallet-transfer-success-page {
  position: relative;
  display: flex;
  width: 100%;
  max-width: 430px;
  min-height: var(--app-viewport-height);
  flex-direction: column;
  margin: 0 auto;
  padding-bottom: env(safe-area-inset-bottom);
  overflow-x: hidden;
  background: #ffffff;
}

.transfer-success-back {
  position: absolute;
  z-index: 2;
  top: calc(env(safe-area-inset-top) + 15px);
  left: 16px;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
}

:deep(.transfer-success-back__icon) {
  width: 11px !important;
  height: 17px !important;
}

.transfer-success-content {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  padding: calc(env(safe-area-inset-top) + 56px) 16px 0;
}

.transfer-success-illustration {
  position: relative;
  width: 124px;
  height: 124px;
  flex: 0 0 124px;
}

:deep(.transfer-success-illustration__glow) {
  position: absolute;
  z-index: 0;
  top: 98px;
  left: 50%;
  width: 108.2px !important;
  height: 30.2px !important;
  transform: translateX(-50%);
  pointer-events: none;
}

.transfer-success-illustration__frame {
  position: relative;
  z-index: 1;
  width: 124px;
  height: 124px;
  overflow: hidden;

  img {
    position: absolute;
    top: -21.93%;
    left: -19.74%;
    display: block;
    width: 140.79%;
    max-width: none;
    height: 140.79%;
    object-fit: cover;
  }
}

.transfer-success-copy {
  display: flex;
  width: min(100%, 301px);
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
  text-align: center;

  h1 {
    display: flex;
    flex-direction: column;
    margin: 0;
    color: #000000;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }

  p {
    margin: 0;
    color: #5b5b5b;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
}

.transfer-success-history-hint {
  position: fixed;
  z-index: 3;
  bottom: calc(env(safe-area-inset-bottom) + 21px);
  left: 50%;
  width: min(283px, calc(100% - 32px));
  margin: 0;
  color: #5b5b5b;
  font-size: 13px;
  font-weight: 400;
  line-height: 13px;
  text-align: left;
  transform: translateX(-50%);
}
</style>
